import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Crown, TelegramLogo, CreditCard, Calendar, Envelope, Lock, Camera, PencilSimple, Check, X, EnvelopeSimple, CheckCircle } from '@phosphor-icons/react';
import UserAvatar from '../components/UserAvatar';

import { API_URL } from '../config';

const ProfilePage = () => {
  const { user, isPremium, isAnnual, refreshUser } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);
  const fileInputRef = useRef(null);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API_URL}/api/auth/upload-avatar`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      await refreshUser();
    } catch (err) {
      console.error('Avatar upload failed:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleSaveName = async () => {
    const trimmed = nameValue.trim();
    if (!trimmed || trimmed === user?.name) { setEditingName(false); return; }
    setSavingName(true);
    try {
      await axios.put(`${API_URL}/api/auth/profile`, { name: trimmed }, { withCredentials: true });
      await refreshUser();
      setEditingName(false);
    } catch (err) {
      console.error('Name update failed:', err);
    } finally {
      setSavingName(false);
    }
  };

  const handleResendVerification = async () => {
    setResendingVerification(true);
    try {
      await axios.post(`${API_URL}/api/auth/resend-verification`, {}, { withCredentials: true });
      alert('Verification email sent! Check your inbox.');
    } catch (err) {
      console.error('Resend verification failed:', err);
    } finally {
      setResendingVerification(false);
    }
  };

  const getSubscriptionBadge = () => {
    if (isAnnual) {
      return (
        <span className="px-3 py-1 bg-[#34C759] text-black font-bold text-sm uppercase">
          Annual Premium
        </span>
      );
    }
    if (isPremium) {
      return (
        <span className="px-3 py-1 bg-[#007AFF] text-white font-bold text-sm uppercase">
          Monthly Premium
        </span>
      );
    }
    return (
      <span className="px-3 py-1 bg-white/10 text-[#8892B0] font-bold text-sm uppercase">
        Free
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-20 pb-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading font-black text-3xl uppercase text-white mb-2" data-testid="profile-title">
            Profile
          </h1>
          <p className="text-[#8892B0]">Manage your account and subscription</p>
        </div>

        {/* Profile Card */}
        <div className="bg-[#131313] border border-white/5 p-8 mb-6" data-testid="profile-card">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              {/* Avatar with upload */}
              <div
                className="relative group cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
                data-testid="avatar-upload-area"
              >
                <UserAvatar src={user?.avatar_url} name={user?.name} size={64} />
                <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <Camera size={20} className="text-white" />
                </div>
                {uploading && (
                  <div className="absolute inset-0 rounded-full bg-black/70 flex items-center justify-center">
                    <span className="text-white text-xs">...</span>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleAvatarUpload}
                  data-testid="avatar-file-input"
                />
              </div>
              <div>
                {editingName ? (
                  <div className="flex items-center gap-2">
                    <input
                      value={nameValue}
                      onChange={(e) => setNameValue(e.target.value)}
                      className="bg-transparent border-b border-[#007AFF] text-white text-xl font-bold outline-none w-48"
                      autoFocus
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSaveName(); if (e.key === 'Escape') setEditingName(false); }}
                      data-testid="edit-name-input"
                    />
                    <button
                      onClick={handleSaveName}
                      disabled={savingName}
                      className="p-1 text-[#34C759] hover:bg-white/10 transition-colors"
                      data-testid="save-name-btn"
                    >
                      <Check size={18} weight="bold" />
                    </button>
                    <button
                      onClick={() => setEditingName(false)}
                      className="p-1 text-[#FF3B30] hover:bg-white/10 transition-colors"
                      data-testid="cancel-name-btn"
                    >
                      <X size={18} weight="bold" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl font-bold text-white">{user?.name}</h2>
                    <button
                      onClick={() => { setNameValue(user?.name || ''); setEditingName(true); }}
                      className="p-1 text-[#5C6B82] hover:text-[#007AFF] transition-colors"
                      data-testid="edit-name-btn"
                    >
                      <PencilSimple size={16} />
                    </button>
                  </div>
                )}
                <p className="text-[#8892B0]">{user?.email}</p>
                <p className="text-xs text-[#5C6B82] mt-1">Click avatar to change</p>
              </div>
            </div>
            {getSubscriptionBadge()}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex items-center gap-3 p-4 bg-[#0A0A0A] border border-white/5">
              <Envelope size={20} className="text-[#8892B0]" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-[#5C6B82] uppercase tracking-wider">Email</p>
                <p className="text-white truncate">{user?.email}</p>
                {user?.email_verified === false ? (
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-[#FF9500]">Not verified</span>
                    <button
                      onClick={handleResendVerification}
                      disabled={resendingVerification}
                      className="text-xs text-[#007AFF] hover:underline disabled:opacity-50"
                      data-testid="profile-resend-verification"
                    >
                      {resendingVerification ? 'Sending...' : 'Resend'}
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 mt-1">
                    <CheckCircle size={12} className="text-[#34C759]" />
                    <span className="text-xs text-[#34C759]">Verified</span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-[#0A0A0A] border border-white/5">
              <Calendar size={20} className="text-[#8892B0]" />
              <div>
                <p className="text-xs text-[#5C6B82] uppercase tracking-wider">Member Since</p>
                <p className="text-white">{formatDate(user?.created_at)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Subscription Card */}
        <div className="bg-[#131313] border border-white/5 p-8 mb-6" data-testid="subscription-card">
          <div className="flex items-center gap-3 mb-6">
            <Crown size={24} className="text-[#007AFF]" />
            <h3 className="font-heading font-bold text-xl uppercase text-white">Subscription</h3>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-[#0A0A0A] border border-white/5">
              <div>
                <p className="text-xs text-[#5C6B82] uppercase tracking-wider">Current Plan</p>
                <p className="text-white font-bold capitalize">{user?.subscription_tier || 'Free'}</p>
              </div>
              {user?.subscription_expires && (
                <div className="text-right">
                  <p className="text-xs text-[#5C6B82] uppercase tracking-wider">Expires</p>
                  <p className="text-white">{formatDate(user?.subscription_expires)}</p>
                </div>
              )}
            </div>

            {!isPremium && (
              <Link
                to="/pricing"
                className="block w-full py-4 text-center bg-[#007AFF] text-white font-bold uppercase tracking-widest hover:bg-[#005bb5] transition-colors"
                data-testid="upgrade-btn"
              >
                Upgrade to Premium
              </Link>
            )}

            {isPremium && !isAnnual && (
              <Link
                to="/pricing"
                className="block w-full py-4 text-center border border-[#34C759] text-[#34C759] font-bold uppercase tracking-widest hover:bg-[#34C759]/10 transition-colors"
                data-testid="upgrade-annual-btn"
              >
                Upgrade to Annual (Save $20 + Human Coaching)
              </Link>
            )}
          </div>
        </div>

        {/* Human Coaching Card - Premium Users */}
        {isPremium && (
          <div className={`${isAnnual ? 'tracing-beam' : 'border border-white/10'} p-[2px]`} data-testid="human-coaching-card">
            <div className="bg-[#131313] p-8">
              <div className="flex items-center gap-3 mb-6">
                <TelegramLogo size={24} weight="fill" className={isAnnual ? 'text-[#34C759]' : 'text-[#007AFF]'} />
                <h3 className="font-heading font-bold text-xl uppercase text-white">Human Coaching</h3>
                <span className={`px-2 py-1 text-xs font-bold uppercase ${
                  isAnnual ? 'bg-[#34C759] text-black' : 'bg-[#007AFF] text-white'
                }`}>
                  {isAnnual ? 'Unlimited' : '1/month'}
                </span>
              </div>

              <p className="text-[#8892B0] mb-2">
                {isAnnual 
                  ? 'As an annual subscriber, you have unlimited human coaching sessions via Telegram. Connect with your personal trading psychology coach anytime.'
                  : 'As a monthly subscriber, you have 1 human coaching session per month via Telegram. Make it count!'}
              </p>
              <p className="text-sm text-[#5C6B82] mb-6">
                <a href={process.env.REACT_APP_TELEGRAM_LINK} target="_blank" rel="noopener noreferrer" className={`hover:underline ${isAnnual ? 'text-[#34C759]' : 'text-[#007AFF]'}`}>Telegram Community</a>
              </p>

              <a
                href={process.env.REACT_APP_TELEGRAM_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center gap-2 px-8 py-4 font-bold uppercase tracking-widest transition-colors ${
                  isAnnual 
                    ? 'bg-[#34C759] text-black hover:bg-[#2db550]'
                    : 'bg-[#007AFF] text-white hover:bg-[#005bb5]'
                }`}
                data-testid="contact-coach-btn"
              >
                <TelegramLogo size={20} weight="fill" />
                Contact Your Coach
              </a>
            </div>
          </div>
        )}

        {/* Locked Telegram Community Card - Free Users */}
        {!isPremium && (
          <div className="border border-white/10 p-[2px] opacity-60" data-testid="locked-coaching-card">
            <div className="bg-[#131313] p-8 relative overflow-hidden">
              <div className="absolute top-4 right-4">
                <Lock size={20} className="text-[#5C6B82]" />
              </div>
              <div className="flex items-center gap-3 mb-6">
                <TelegramLogo size={24} weight="fill" className="text-[#5C6B82]" />
                <h3 className="font-heading font-bold text-xl uppercase text-white">Telegram Community</h3>
                <span className="px-2 py-1 text-xs font-bold uppercase bg-white/10 text-[#5C6B82]">
                  Locked
                </span>
              </div>
              <p className="text-[#5C6B82] mb-6">
                Upgrade to Premium to unlock human coaching sessions via Telegram with a personal trading psychology coach.
              </p>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 px-8 py-4 border border-white/20 text-[#8892B0] font-bold uppercase tracking-widest hover:bg-white/5 transition-colors"
                data-testid="unlock-coaching-btn"
              >
                <Lock size={18} />
                Upgrade to Unlock
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
